<script setup>
import { useDeliveryPlatformsStore } from '@/stores/deliveryPlatforms'
import { useOrderTypeConfigStore } from '@/stores/orderTypeConfigs'
import { usePosStore } from '@/stores/pos'
import { useTablesStore } from '@/stores/tables'
import { useApi } from '@/utils/api'
import PosModifierDialog from '@/components/pos/PosModifierDialog.vue'

definePage({
  meta: {
    layout: 'pos',
    action: 'create',
    subject: 'Sales',
  },
})

const { formatRupiah } = useFormat()
const { loading: checkoutLoading, request, lastError } = useApiRequest()

const showSnackbar = (text, color = 'success') => {
  snackbar.value = { show: true, text, color }
}
const posStore = usePosStore()
const tablesStore = useTablesStore()
const orderTypeConfigStore = useOrderTypeConfigStore()
const deliveryPlatformsStore = useDeliveryPlatformsStore()
const route = useRoute()
const router = useRouter()

// ── State: produk & kategori ──────────────────────────────────────────────
const variants = ref([])
const categories = ref([])
const selectedCategory = ref('all')
const selectedVariant = ref('all')  
const search = ref('')
const loadingProducts = ref(false)

const searchInput = ref(null)

// ─ State: dialog ──────────────────────────────────────────────────────────
const openShiftDialog = ref(false)
const paymentDialog = ref(false)
const receiptDialog = ref(false)
const lastOrder = ref(null)
const lastChangeAmount = ref(0)
const clearCartDialog = ref(false)

// ─ State: QRIS Otomatis (Modul 18 — Midtrans/Xendit via gateway) ──────────
// Berbeda dari opsi "QRIS" manual di PosPaymentDialog (kasir input reference
// number sendiri setelah bayar via EDC) — ini generate QR live & realtime.
const qrisPaymentDialog = ref(false)
const qrisOrderId = ref(null)
const qrisCreating = ref(false)
const tenantId = useTenantId()

const snackbar = ref({ show: false, text: '', color: 'success' })

// ── Opsi filter varian ────────────────────────────────────────────────────
const variantOptions = [
  { value: 'all', label: 'Semua' },
  { value: 'Hot', label: 'Hot' },
  { value: 'Iced', label: 'Iced' },
  { value: 'Regular', label: 'Regular' },
  { value: 'Large', label: 'Large' },
]

// ═══════════════════════════════════════════════════════════════════════
// MODUL 14 — Order Type (Dine-in / Takeaway / Delivery)
// ═══════════════════════════════════════════════════════════════════════
const orderTypeOptions = [
  { value: 'dine_in', label: 'Dine-in', icon: 'ri-restaurant-line' },
  { value: 'takeaway', label: 'Takeaway', icon: 'ri-shopping-bag-line' },
  { value: 'delivery', label: 'Delivery', icon: 'ri-motorbike-line' },
]

const orderType = ref('takeaway')
const deliveryPlatformId = ref(null)

const isDineIn = computed(() => orderType.value === 'dine_in')
const isDelivery = computed(() => orderType.value === 'delivery')

// ── Modul 13 — Konteks Meja (dine-in) ────────────────────────────────────
// Dibawa dari pages/tables/index.vue lewat query ?table_id=&table_session_id=
// saat kasir klik "Buka Sesi" atau "Tambah Pesanan" pada meja tertentu.
const selectedTable = ref(null) // objek Table lengkap (untuk tampilan chip)
const selectedTableSessionId = ref(null)
const tablePickerDialog = ref(false)
const moveTableDialogVisible = ref(false)
const splitBillDialog = ref(false)

const loadTableContextFromQuery = async () => {
  const tableId = route.query.table_id ? Number(route.query.table_id) : null
  if (!tableId) return

  if (tablesStore.tables.length === 0) await tablesStore.fetchTables()

  const table = tablesStore.tables.find(t => t.id === tableId)
  if (table) {
    selectedTable.value = table
    selectedTableSessionId.value = route.query.table_session_id
      ? Number(route.query.table_session_id)
      : table.current_table_session_id
    orderType.value = 'dine_in'
  }
}

const openTablePicker = async () => {
  if (tablesStore.tables.length === 0) await tablesStore.fetchTables()
  tablePickerDialog.value = true
}

const chooseTable = table => {
  selectedTable.value = table
  selectedTableSessionId.value = table.current_table_session_id
  tablePickerDialog.value = false

  router.replace({
    name: 'pos',
    query: { table_id: table.id, table_session_id: table.current_table_session_id },
  })
}

const clearTableContext = () => {
  selectedTable.value = null
  selectedTableSessionId.value = null
  router.replace({ name: 'pos' })
}

// Meja yang bisa dipilih: yang masih available, ATAU meja yang sedang jadi
// konteks order saat ini (occupied tapi memang meja yang sedang dipakai)
const pickableTables = computed(() => {
  const list = [...tablesStore.availableTables]
  if (selectedTable.value && !list.some(t => t.id === selectedTable.value.id)) {
    list.push(selectedTable.value)
  }
  return list
})

// Saat kasir ganti tab tipe order secara manual:
// - pindah KE dine_in tanpa lewat halaman Denah Meja -> biarkan kasir pilih meja sendiri
// - pindah KELUAR dari dine_in -> lepas konteks meja
// - pindah KELUAR dari delivery -> lepas platform yang dipilih
watch(orderType, (newType, oldType) => {
  if (oldType === 'dine_in' && newType !== 'dine_in') {
    selectedTable.value = null
    selectedTableSessionId.value = null
  }
  if (oldType === 'delivery' && newType !== 'delivery') {
    deliveryPlatformId.value = null
  }
})

// ── Modul 14 — Delivery Platform & estimasi surcharge ────────────────────
const surchargeEstimate = computed(() =>
  orderTypeConfigStore.estimateSurcharge(orderType.value, posStore.subtotal)
)

const surchargeConfig = computed(() => orderTypeConfigStore.getConfig(orderType.value))

// ── Fetch produk + variant aktif ──────────────────────────────────────────
const fetchVariants = async () => {
  loadingProducts.value = true
  try {
    const response = await useApi('/api/v1/products', {
      query: {
        is_active: 1,
        per_page: 200,
        search: search.value || undefined,
        category_id: selectedCategory.value !== 'all' ? selectedCategory.value : undefined,
      },
    }).get()
    const data = response?.data ?? response

    // Flatten products -> variants
    const products = data?.data ?? []
    const allVariants = products.flatMap(product =>
      (product.variants ?? []).map(variant => ({
        ...variant,
        image_url: product.image_url ?? null, 
        product: { id: product.id, name: product.name, modifier_groups: product.modifier_groups ?? [] },
        category_id: product.category_id,
      }))
    )

    if (selectedVariant.value !== 'all') {
      variants.value = allVariants.filter(v => 
        v.name?.toLowerCase() === selectedVariant.value.toLowerCase()
      )
    } else {
      variants.value = allVariants
    }
  } finally {
    loadingProducts.value = false
  }
}

const fetchCategories = async () => {
  const response = await useApi('/api/v1/categories', {
    query: { per_page: 100 },
  }).get()
  const data = response?.data ?? response
  
  // ✅ FIX: data bisa berupa array langsung atau { data: [...] }
  categories.value = Array.isArray(data) ? data : (data?.data ?? [])
}

let searchDebounce
watch(search, () => {
  clearTimeout(searchDebounce)
  searchDebounce = setTimeout(fetchVariants, 350)
})

watch([selectedCategory, selectedVariant], fetchVariants)  // ✅ Watch kedua filter

// ─ Init: cek shift aktif ─────────────────────────────────────────────────
const initPos = async () => {
  try {
    await posStore.fetchActiveShift()
  } catch (err) {
    console.error('[POS] fetchActiveShift gagal:', err)
  }

  if (!posStore.hasActiveShift) {
    openShiftDialog.value = true
  }

  try {
    await Promise.all([
      fetchVariants(),
      fetchCategories(),
      loadTableContextFromQuery(),
      orderTypeConfigStore.fetchConfigs(),
      deliveryPlatformsStore.fetchPlatforms(),
    ])
  } catch (err) {
    console.error('[POS] fetchVariants/fetchCategories/tableContext/orderType gagal:', err)
  }
}

onMounted(() => {
  initPos()
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})

// ── Cart actions ─────────────────────────────────────────────
const modifierDialog = ref(false)
const pendingVariant = ref(null)
const pendingGroups = ref([])

const handleAddVariant = variant => {
  const groups = variant.product?.modifier_groups ?? []

  if (groups.length > 0) {
    pendingVariant.value = variant
    pendingGroups.value = groups
    modifierDialog.value = true
  } else {
    posStore.addItem(variant)
  }
}

const handleModifierConfirm = ({ modifierIds, adjustment, groups }) => {
  posStore.addItem(pendingVariant.value, { modifierIds, adjustment, groups })
}

const handleShiftOpened = shift => {
  posStore.setActiveShift(shift)
}

// ── Checkout ─────────────────────────────────────────────────────────────
const canCheckout = computed(() =>
  posStore.cart.length > 0 &&
  posStore.hasActiveShift &&
  (!isDelivery.value || !!deliveryPlatformId.value)
)

const openPaymentDialog = () => {
  if (!canCheckout.value) return
  paymentDialog.value = true
}

const handleConfirmPayment = async ({ payments, change_amount }) => {
  const res = await request(() =>
    useApi('/api/v1/pos/checkout').post({
      body: {
        customer_name: posStore.customerName || null,
        discount_amount: posStore.discountAmount || 0,
        tax_rate: posStore.taxRate || undefined,
        items: posStore.buildCheckoutItems(),
        payments,
        order_type: orderType.value,
        table_id: isDineIn.value ? (selectedTable.value?.id ?? null) : null,
        table_session_id: isDineIn.value ? (selectedTableSessionId.value ?? null) : null,
        delivery_platform_id: isDelivery.value ? deliveryPlatformId.value : null,
      },
    })
  )

  if (res) {
    const data = res?.data ?? res
    lastOrder.value = data
    lastChangeAmount.value = change_amount
    paymentDialog.value = false
    posStore.clearCart()
    deliveryPlatformId.value = null

    // ✅ UBAH: dulu selalu buka modal ReceiptPrinter dulu.
    // Sekarang langsung buka halaman /pos/receipt/{id} di tab baru —
    // dipanggil sesaat setelah await (masih dalam user gesture yang sama),
    // jadi umumnya tidak diblokir popup blocker.
    const receiptWindow = window.open(`/pos/receipt/${data.id}`, '_blank')

    if (!receiptWindow) {
      // Fallback: popup diblokir browser. Tampilkan modal supaya kasir
      // masih bisa buka struk lewat klik manual (synchronous user gesture).
      showSnackbar('Popup diblokir browser. Klik "Cetak Struk" di bawah untuk buka struk.', 'warning')
      receiptDialog.value = true
    } else if (isDineIn.value) {
      // Dine-in tetap perlu modal ini untuk aksi lanjutan (Pindah Meja / Split Bill).
      receiptDialog.value = true
    }
  } else {
    if (lastError.value) {
      showSnackbar(lastError.value.message, lastError.value.type ?? 'error')
    }
  }
}

// ── QRIS Otomatis (gateway Midtrans/Xendit) ────────────────────────────────
const openQrisPayment = async () => {
  if (!canCheckout.value || qrisCreating.value) return

  qrisCreating.value = true
  try {
    const res = await useApi('/api/v1/pos/pending-orders').post({
      body: {
        customer_name: posStore.customerName || null,
        discount_amount: posStore.discountAmount || 0,
        tax_rate: posStore.taxRate || undefined,
        items: posStore.buildCheckoutItems(),
        order_type: orderType.value,
        table_id: isDineIn.value ? (selectedTable.value?.id ?? null) : null,
        table_session_id: isDineIn.value ? (selectedTableSessionId.value ?? null) : null,
      },
    })
    const data = res?.data ?? res

    qrisOrderId.value = data.id
    qrisPaymentDialog.value = true
  } catch (err) {
    showSnackbar(err?.data?.message || err?.message || 'Gagal membuat order untuk QRIS.', 'error')
  } finally {
    qrisCreating.value = false
  }
}

// Order sudah dikonfirmasi 'settlement' oleh PosQrisPaymentDialog (realtime/polling)
const handleQrisPaid = ({ orderId }) => {
  qrisPaymentDialog.value = false
  posStore.clearCart()

  const receiptWindow = window.open(`/pos/receipt/${orderId}`, '_blank')

  if (!receiptWindow) {
    showSnackbar('Popup diblokir browser. Buka Riwayat Order untuk cetak struk.', 'warning')
  }
}

const handleQrisClosed = () => {
  qrisOrderId.value = null
}

const handleReceiptClosed = () => {
  lastOrder.value = null
  searchInput.value?.focus?.()
}

const handleSplitSettled = () => {
  splitBillDialog.value = false
}

const handleTableMoved = updatedOrder => {
  lastOrder.value = updatedOrder
}

// ── Keyboard shortcuts ───────────────────────────────────────────────────
const handleKeydown = e => {
  if (e.key === 'F1') {
    e.preventDefault()
    searchInput.value?.focus?.()
  } else if (e.key === 'F2') {
    e.preventDefault()
    openPaymentDialog()
  } else if (e.key === 'F3') {
    e.preventDefault()
    if (posStore.cart.length > 0) clearCartDialog.value = true
  } else if (e.key === 'Escape') {
    paymentDialog.value = false
  }
}

const confirmClearCart = () => {
  posStore.clearCart()
  clearCartDialog.value = false
}
</script>

<template>
  <div class="pos-container">
    <VRow no-gutters class="fill-height">
      <!-- ── LEFT PANEL: Grid Produk ─────────────────────────────────── -->
      <VCol cols="12" md="8" class="pos-products pa-4">
        <VTextField
          ref="searchInput"
          v-model="search"
          placeholder="Cari produk... (F1)"
          prepend-inner-icon="ri-search-line"
          density="comfortable"
          variant="outlined"
          clearable
          autofocus
          class="mb-3"
          hide-details
        />

        <!-- Filter Kategori -->
      <VChipGroup
        v-model="selectedCategory"
        mandatory
        class="mb-2"
      >
        <VChip value="all" filter color="grey">Semua</VChip>
        <VChip
          v-for="cat in categories"
          :key="cat.id"
          :value="cat.id"
          filter
          :color="cat.color || 'primary'"
        >
          {{ cat.name }}
        </VChip>
      </VChipGroup>

      <!-- Filter Varian -->
      <VChipGroup
        v-model="selectedVariant"
        mandatory
        class="mb-3"
      >
        <VChip
          v-for="opt in variantOptions"
          :key="opt.value"
          :value="opt.value"
          filter
          :color="opt.value === 'all' ? 'grey' : (opt.value === 'Hot' ? 'error' : opt.value === 'Iced' ? 'info' : opt.value === 'Large' ? 'warning' : 'success')"
        >
          {{ opt.label }}
        </VChip>
      </VChipGroup>

        <div v-if="loadingProducts" class="d-flex justify-center pa-8">
          <VProgressCircular indeterminate color="primary" />
        </div>

        <div v-else-if="variants.length === 0" class="text-center pa-8">
          <VIcon icon="ri-cup-line" size="56" color="grey-lighten-1" />
          <p class="text-body-1 mt-3 text-medium-emphasis">
            Tidak ada produk ditemukan
          </p>
        </div>

        <VRow v-else dense class="pos-product-grid">
          <VCol
            v-for="variant in variants"
            :key="variant.id"
            cols="6"
            sm="4"
            md="3"
            lg="3"
          >
            <PosProductCard :variant="variant" @add="handleAddVariant" />
                    </VCol>
        </VRow>
      </VCol>

      <!-- ── RIGHT PANEL: Cart & Payment ─────────────────────────────── -->
      <VCol cols="12" md="4" class="pos-cart d-flex flex-column">
        <!-- Cart header -->
        <div class="pos-cart__header pa-4">
          <div class="d-flex align-center justify-space-between">
            <span class="text-subtitle-1 font-weight-medium">
              <VIcon icon="ri-shopping-basket-line" class="mr-1" />
              {{ posStore.itemCount }} item
            </span>
            <VBtn
              v-if="posStore.cart.length > 0"
              size="small"
              variant="text"
              color="error"
              @click="clearCartDialog = true"
            >
              Kosongkan (F3)
            </VBtn>
          </div>

          <!-- Modul 14: toggle tipe order -->
          <VBtnToggle
            v-model="orderType"
            color="primary"
            density="comfortable"
            mandatory
            class="mt-2 mb-2"
            style="width: 100%"
          >
            <VBtn
              v-for="opt in orderTypeOptions"
              :key="opt.value"
              :value="opt.value"
              :prepend-icon="opt.icon"
              size="small"
              style="flex: 1"
            >
              {{ opt.label }}
            </VBtn>
          </VBtnToggle>

          <!-- Modul 13: konteks meja (khusus dine-in) -->
          <div v-if="isDineIn" class="d-flex align-center gap-2 flex-wrap mb-2">
            <VChip
              v-if="selectedTable"
              color="primary"
              variant="tonal"
              closable
              prepend-icon="ri-table-2"
              @click:close="clearTableContext"
            >
              Meja {{ selectedTable.code }}
            </VChip>
            <VBtn size="small" variant="outlined" prepend-icon="ri-table-2" @click="openTablePicker">
              {{ selectedTable ? 'Ganti Meja' : 'Pilih Meja' }}
            </VBtn>
          </div>

          <!-- Modul 14: pilih platform delivery (khusus delivery) -->
          <VSelect
            v-if="isDelivery"
            v-model="deliveryPlatformId"
            :items="deliveryPlatformsStore.activePlatforms"
            item-title="name"
            item-value="id"
            label="Platform Delivery"
            density="compact"
            variant="outlined"
            hide-details
            class="mb-2"
            :rules="[requiredValidator]"
          >
            <template #no-data>
              <div class="pa-2 text-caption text-medium-emphasis">
                Belum ada platform aktif. Tambahkan di Pengaturan &gt; Platform Delivery.
              </div>
            </template>
          </VSelect>

          <VTextField
            v-model="posStore.customerName"
            placeholder="Nama pelanggan (opsional)"
            density="compact"
            variant="outlined"
            hide-details
            class="mt-2"
          />
        </div>

        <VDivider />

        <!-- Cart items -->
        <div class="pos-cart__items flex-grow-1 px-3">
          <div v-if="posStore.cart.length === 0" class="text-center pa-8">
            <VIcon icon="ri-shopping-cart-line" size="48" color="grey-lighten-1" />
            <p class="text-body-2 mt-2 text-medium-emphasis">
              Keranjang masih kosong.<br>Klik produk di sebelah kiri untuk menambah.
            </p>
          </div>

          <PosCartItem
            v-for="(item, index) in posStore.cart"
            :key="`${item.variant_id}-${index}`"
            :item="item"
            :index="index"
            @increment="posStore.incrementItem"
            @decrement="posStore.decrementItem"
            @remove="posStore.removeItem"
            @update-notes="posStore.setItemNotes"
          />
        </div>

        <VDivider />

        <!-- Discount & Total -->
        <div class="pos-cart__summary pa-4">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-body-2">Diskon</span>
            <VTextField
              v-model.number="posStore.discountAmount"
              type="number"
              density="compact"
              variant="outlined"
              hide-details
              prefix="Rp"
              style="max-width: 140px"
            />
          </div>

          <div class="d-flex justify-space-between text-body-2 mb-1">
            <span>Subtotal</span>
            <span>{{ formatRupiah(posStore.subtotal) }}</span>
          </div>
          <div
            v-if="posStore.taxAmount > 0"
            class="d-flex justify-space-between text-body-2 mb-1"
          >
            <span>Pajak</span>
            <span>{{ formatRupiah(posStore.taxAmount) }}</span>
          </div>

          <!-- Modul 14: preview service charge / markup tipe order (estimasi client-side) -->
          <div
            v-if="surchargeConfig?.is_active && surchargeEstimate > 0"
            class="d-flex justify-space-between text-body-2 mb-1"
          >
            <span>
              Biaya {{ orderTypeOptions.find(o => o.value === orderType)?.label }}
              <span class="text-caption text-medium-emphasis">
                ({{ surchargeConfig.charge_type === 'percentage' ? `${surchargeConfig.charge_value}%` : 'tetap' }})
              </span>
            </span>
            <span>{{ formatRupiah(surchargeEstimate) }}</span>
          </div>

          <div class="d-flex justify-space-between text-h6 font-weight-bold mt-2">
            <span>Total</span>
            <span class="text-primary">{{ formatRupiah(posStore.total + surchargeEstimate) }}</span>
          </div>
          <p class="text-caption text-medium-emphasis text-right mb-0">
            *Total akhir termasuk biaya layanan dihitung ulang oleh server saat checkout
          </p>

          <VBtn
            block
            size="large"
            color="primary"
            class="mt-4"
            :disabled="!canCheckout"
            @click="openPaymentDialog"
          >
            Bayar (F2)
          </VBtn>

          <VBtn
            block
            size="large"
            variant="tonal"
            color="primary"
            class="mt-2"
            prepend-icon="ri-qr-code-line"
            :disabled="!canCheckout"
            :loading="qrisCreating"
            @click="openQrisPayment"
          >
            QRIS Otomatis
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <!-- Dialogs -->
    <OpenShiftDialog v-model="openShiftDialog" @opened="handleShiftOpened" />

    <PosPaymentDialog
      v-model="paymentDialog"
      :total="posStore.total + surchargeEstimate"
      :loading="checkoutLoading"
      @confirm="handleConfirmPayment"
    />

    <PosQrisPaymentDialog
      v-model="qrisPaymentDialog"
      :order-id="qrisOrderId ?? 0"
      :tenant-id="tenantId"
      :amount="posStore.total + surchargeEstimate"
      payment-type="qris"
      @paid="handleQrisPaid"
      @closed="handleQrisClosed"
    />

    <ReceiptPrinter
      v-model="receiptDialog"
      :order="lastOrder"
      :change-amount="lastChangeAmount"
      @close="handleReceiptClosed"
    >
      <!-- Modul 13: aksi tambahan setelah order dine-in dibuat -->
      <template v-if="isDineIn && lastOrder" #actions>
        <VBtn
          variant="tonal"
          color="secondary"
          prepend-icon="ri-arrow-left-right-line"
          @click="moveTableDialogVisible = true"
        >
          Pindah Meja
        </VBtn>
        <VBtn
          variant="tonal"
          color="primary"
          prepend-icon="ri-scissors-cut-line"
          @click="splitBillDialog = true"
        >
          Split Bill
        </VBtn>
      </template>
    </ReceiptPrinter>

    <ConfirmDialog
      v-model="clearCartDialog"
      title="Kosongkan Keranjang?"
      message="Semua item di keranjang akan dihapus."
      confirm-text="Ya, Kosongkan"
      color="error"
      @confirm="confirmClearCart"
    />

    <!-- Modul 13: Table picker sederhana -->
    <VDialog v-model="tablePickerDialog" max-width="420">
      <VCard>
        <VCardTitle class="text-h6">Pilih Meja</VCardTitle>
        <VCardText>
          <VList v-if="pickableTables.length > 0">
            <VListItem
              v-for="table in pickableTables"
              :key="table.id"
              :title="`${table.code} — ${table.name || 'Tanpa nama'}`"
              :subtitle="table.area_name"
              @click="chooseTable(table)"
            />
          </VList>
          <VAlert v-else type="info" variant="tonal">
            Tidak ada meja tersedia. Buka sesi meja dari halaman Denah Meja.
          </VAlert>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" @click="tablePickerDialog = false">Tutup</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <MoveTableDialog
      v-if="lastOrder"
      v-model="moveTableDialogVisible"
      :order-id="lastOrder.id"
      :current-table-id="selectedTable?.id"
      @moved="handleTableMoved"
    />

    <SplitBillDialog
      v-if="lastOrder"
      v-model="splitBillDialog"
      :order="lastOrder"
      @settled="handleSplitSettled"
    />

    <PosModifierDialog
      v-model="modifierDialog"
      :variant="pendingVariant"
      :modifier-groups="pendingGroups"
      @confirm="handleModifierConfirm"
    />
      <VSnackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" location="top">
        {{ snackbar.text }}
        <template #actions>
          <VBtn variant="text" @click="snackbar.show = false">Tutup</VBtn>
        </template>
      </VSnackbar>
  </div>
</template>

<style scoped>
/* ═══════════════════════════════════════════════════════════════════════
   POS LAYOUT — Fixed header + scroll area + sticky payment
   ═══════════════════════════════════════════════════════════════════════ */

.pos-container {
  height: 100%;
  overflow: hidden; /* ← Kunci: tidak ada scroll global */
}

.fill-height {
  height: 100%;
}

/* ── LEFT: Product grid scroll internal ─────────────────────────── */
.pos-products {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

/* ── RIGHT: Cart column (flex: header + items + summary) ────────── */
.pos-cart {
  height: 100%;
  background: rgb(var(--v-theme-surface));
  border-left: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pos-cart__header {
  flex-shrink: 0; /* Fixed di atas */
}

.pos-cart__items {
  flex: 1;           /* Ambil sisa space */
  min-height: 0;     /* ← KUNCI: agar flex child bisa scroll */
  overflow-y: auto;  /* Scroll internal */
  overflow-x: hidden;
}

.pos-cart__summary {
  flex-shrink: 0;    /* ← Fixed di bawah, TIDAK scroll */
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background: rgb(var(--v-theme-surface));
}

/* ── MODERATE TEXT SIZE (sedikit lebih besar, tidak oversized) ──── */
:deep(.pos-cart__header .text-subtitle-1) {
  font-size: 1.05rem !important;
  font-weight: 600;
}

:deep(.pos-cart__summary .text-body-2) {
  font-size: 0.95rem !important;
}

:deep(.pos-cart__summary .text-h6) {
  font-size: 1.25rem !important;
  font-weight: 700;
}

:deep(.pos-cart__summary .v-btn--size-large) {
  height: 52px;
  font-size: 1.05rem;
  font-weight: 600;
}

/* ── Scrollbar lebih halus ──────────────────────────────────────── */
.pos-products::-webkit-scrollbar,
.pos-cart__items::-webkit-scrollbar {
  width: 6px;
}

.pos-products::-webkit-scrollbar-thumb,
.pos-cart__items::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-on-surface), 0.15);
  border-radius: 3px;
}

/* ── Responsive mobile ──────────────────────────────────────────── */
@media (max-width: 960px) {
  /* Paksa stacking vertikal yang benar: produk di atas dengan tinggi
     TETAP (biar sisa layar pasti kebagian ke cart), cart di bawah
     mengambil sisa ruang (flex:1) supaya list item punya tempat nyata,
     bukan sisa remah setelah header cart (toggle order type + nama). */
  .fill-height {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    height: 100%;
  }

  .pos-products {
    flex: 0 0 34vh !important;
    height: 34vh;
    max-height: 34vh;
    min-height: 34vh;
    max-width: 100% !important;
  }

  .pos-cart {
    flex: 1 1 auto !important;
    height: auto;
    min-height: 0;
    max-width: 100% !important;
    border-left: none;
    border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  }

  .pos-cart__header {
    padding: 10px 16px !important;
  }

  .pos-cart__items {
    min-height: 96px; /* jaminan list item tidak collapse ke 0 */
  }
}

/* ── Responsive HANDPHONE (320px–600px) ───────────────────────────
   Perbaikan khusus: tombol Bayar & QRIS sempat tidak muat/terpotong
   karena .pos-cart pakai overflow:hidden sementara konten header
   (toggle order type + meja + nama pelanggan) + ringkasan (diskon/
   subtotal/total + 2 tombol) lebih tinggi dari sisa layar.

   Strategi: alih-alih mengandalkan perhitungan tinggi yang presisi
   (rawan berubah tergantung isi cart), seluruh panel cart dibuat bisa
   di-scroll, dan bagian ringkasan (.pos-cart__summary) dibuat
   position: sticky di bawah viewport. Hasilnya: tombol Bayar & QRIS
   SELALU terlihat menempel di bawah layar, apa pun isi keranjangnya —
   tidak lagi bergantung pas/tidaknya perhitungan tinggi.               */
@media (max-width: 600px) {
  .pos-container {
    overflow-y: auto !important;
    overflow-x: hidden;
  }

  .fill-height {
    height: auto;
    min-height: 100%;
  }

  /* Grid produk: tinggi maksimum, scroll internal sendiri (perilaku sama
     seperti sebelumnya), hanya dipadatkan sedikit agar cart dapat porsi
     lebih banyak. */
  .pos-products {
    flex: 0 0 auto !important;
    height: auto !important;
    max-height: 30vh !important;
    min-height: 160px !important;
    overflow-y: auto;
    padding: 8px !important;
  }

  /* Cart: tidak lagi overflow:hidden yang memotong konten, biarkan
     tumbuh natural — halaman (.pos-container) yang scroll. */
  .pos-cart {
    flex: 1 1 auto !important;
    height: auto !important;
    overflow: visible !important;
  }

  .pos-cart__header {
    padding: 8px 12px !important;
  }

  .pos-cart__items {
    flex: 1 1 auto;
    overflow-y: visible; /* ikut scroll halaman, bukan scroll internal */
    min-height: 0 !important;
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  /* KUNCI perbaikan: ringkasan + tombol Bayar/QRIS nempel di bawah
     viewport, selalu terlihat berapa pun tinggi konten di atasnya. */
  .pos-cart__summary {
    position: sticky;
    bottom: 0;
    z-index: 5;
    padding: 10px 12px !important;
    background: rgb(var(--v-theme-surface));
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.12);
  }

  /* Padatkan sedikit elemen di header cart */
  :deep(.pos-cart__header .v-btn-group),
  :deep(.pos-cart__header .v-btn-toggle) {
    height: 34px !important;
  }

  :deep(.pos-cart__header .v-btn-toggle .v-btn) {
    font-size: 0.72rem !important;
    padding: 0 6px !important;
    min-width: 0 !important;
  }

  :deep(.pos-cart__header .v-btn-toggle .v-btn .v-icon) {
    font-size: 1rem !important;
  }

  .pos-cart__header .mt-2 {
    margin-top: 6px !important;
  }

  .pos-cart__header .mb-2 {
    margin-bottom: 6px !important;
  }

  /* Padatkan baris diskon/subtotal/total */
  .pos-cart__summary .mb-2 {
    margin-bottom: 4px !important;
  }

  .pos-cart__summary .mb-1 {
    margin-bottom: 2px !important;
  }

  .pos-cart__summary .mt-2 {
    margin-top: 4px !important;
  }

  :deep(.pos-cart__summary .text-h6) {
    font-size: 1.05rem !important;
  }

  .pos-cart__summary .text-caption {
    font-size: 0.65rem !important;
    line-height: 1.1;
  }

  /* Tombol Bayar & QRIS dipadatkan tapi tetap nyaman ditekan (≥40px) */
  :deep(.pos-cart__summary .v-btn--size-large) {
    height: 42px !important;
    font-size: 0.92rem !important;
  }

  .pos-cart__summary .mt-4 {
    margin-top: 8px !important;
  }

  .pos-cart__summary .mt-2 {
    margin-top: 6px !important;
  }
}
</style>